import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCallStore } from '@/stores/call';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { CHAT_TEXT_MULTIPLIERS } from '@/utils/constants/chat';

export const usePreferences = () => {
  const toastStore = useToastStore();
  const userStore = useUserStore();
  const callStore = useCallStore();
  const { t } = useI18n();

  const isGsc = computed(() => {
    return !!callStore.qrCodeInfo.options.screenHeight;
  });

  const validateTextSize = (textSizeMultiplier) => {
    const incomingTextSize = parseFloat(textSizeMultiplier);
    const isTooBig = incomingTextSize > CHAT_TEXT_MULTIPLIERS[9];
    const isTooSmall = incomingTextSize < CHAT_TEXT_MULTIPLIERS[0];

    if (isTooBig || isTooSmall) {
      const fallBack = isTooSmall ? CHAT_TEXT_MULTIPLIERS[0] : CHAT_TEXT_MULTIPLIERS[9];
      return CHAT_TEXT_MULTIPLIERS[Math.round(incomingTextSize)] ?? fallBack;
    }

    return textSizeMultiplier;
  };

  const assimilateUserPreferences = (newVal) => {
    if (!isValidPreference(newVal)) return false;

    if (newVal.textSizeMultiplier) {
      newVal.textSizeMultiplier = validateTextSize(newVal.textSizeMultiplier);
    }

    userStore.userPreferences = {
      ...userStore.userPreferences,
      ...newVal,
    };

    return true;
  };

  const updateRemotePreferences = (newVal) => {
    newVal.textSizeMultiplier = validateTextSize(newVal);

    userStore.remotePreferences = { ...userStore.remotePreferences, ...newVal };
  };

  const isValidPreference = (newVal) => {
    let isValid = true;
    let errorPreference = '';
    let errorMessage = '';
    let minimum = 20;

    Object.entries(newVal).forEach(([key, value]: [string, any]) => {
      switch (key) {
        case 'textSizeMultiplier':
          break;
        case 'screenMessageWidth':
          if (value < 20) {
            isValid = false;
            errorPreference = 'screen_mode_message_width';
            errorMessage = 'error_input_too_small';
          }
          break;
        case 'screenScrollHeight':
          break;
        case 'screenContainerHeight':
          if (!isGsc.value) break;
          if (value * 16 < 500) {
            isValid = false;
            errorPreference = 'screen_mode_container_height';
            errorMessage = 'error_input_too_small';
            minimum = 500;
          }
          break;
        case 'subtitleOpacity':
          break;
        case 'subtitleLocationY':
          break;
        case 'subtitleLineHeight':
          break;
        case 'subtitleLineCount':
          break;
        case 'subtitleWidth':
          if (value < 20) {
            isValid = false;
            errorPreference = 'subtitle_mode_message_width';
            errorMessage = 'error_input_too_small';
            minimum = 20;
          }
          break;
        case 'subtitleBackgroundColor':
          if (!['GREEN', 'BLACK'].includes(value.toUpperCase())) {
            isValid = false;
            errorPreference = 'screen_color';
            errorMessage = 'green_or_black';
          }
          break;
        case 'voiceSize':
          break;
      }
    });

    if (!isValid) {
      toastStore.error(
        t(errorMessage, {
          errorTarget: t(errorPreference),
          minimum: minimum,
        })
      );
    }

    return isValid;
  };

  return { isValidPreference, assimilateUserPreferences, updateRemotePreferences };
};
